import { BarController, BarElement, CategoryScale, ChartData, Chart as ChartJS, ChartOptions, Filler, Legend, LinearScale, LineController, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import 'chart.js/auto';
import React from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';

/** Premium winery chart color palette */
export const MONDAY_CHART_PALETTE = ['#B4233A', '#B77A2A', '#3F8A4E', '#3F718A', '#7A172A', '#DCA34A', '#C73340'];

export const METRIC_COLOR_MAP = {
    brix: '#B4233A',
    baume: '#B77A2A',
    ph: '#3F718A',
    ta: '#3F8A4E',
    sg: '#7A172A',
    temperature: '#DCA34A',
    alcohol: '#B77A2A',
    totalSo2: '#3F718A',
    freeSo2: '#3F8A4E',
    rs: '#C73340',
    bisulfite: '#7A172A',
} as const;

interface ResponsiveChartProps {
    type: 'bar' | 'line' | 'doughnut' | 'pie' | 'radar' | 'polarArea';
    data: ChartData<any>;
    options?: ChartOptions<any>;
    height?: string;
    className?: string;
    title?: string;
    loading?: boolean;
    instanceKey?: string;
    variant?: 'card' | 'embedded';
}

// Register ChartJS core components once
let chartJsRegistered = false;
function ensureChartRegistration() {
    if (!chartJsRegistered) {
        try {
            ChartJS.register(
                LineController,
                BarController,
                CategoryScale,
                LinearScale,
                PointElement,
                LineElement,
                BarElement,
                Title,
                Tooltip,
                Legend,
                Filler
            );
        } catch (e) {
            // ignore if already registered
        }
        chartJsRegistered = true;
    }
}

/** Convert a hex color like #B4233A to #B4233A0D (5% opacity) */
function hexToFillColor(hex: string): string {
    return hex + '0D';
}

class ChartErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {/* no-op logging hook */ }
    render() {
        if (this.state.hasError) {
            return (
                <div className="bg-white rounded-lg shadow border border-monday-border-light p-4">
                    <p className="text-sm text-error-500">Unable to render chart.</p>
                </div>
            );
        }
        return this.props.children as any;
    }
}

const ResponsiveChart: React.FC<ResponsiveChartProps> = ({
    type,
    data,
    options = {},
    height = '400px',
    className = '',
    title,
    loading = false,
    instanceKey,
    variant = 'card'
}) => {
    ensureChartRegistration();

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // Mobile-optimized chart options with premium winery styling
    const mobileOptions: ChartOptions<any> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index' as const,
            axis: 'x' as const,
            intersect: false,
        },
        plugins: {
            title: {
                display: false,
            },
            legend: {
                position: isMobile ? 'bottom' : 'top',
                align: isMobile ? 'center' : 'end',
                labels: {
                    padding: isMobile ? 10 : 16,
                    usePointStyle: true,
                    boxWidth: 8,
                    boxHeight: 8,
                    font: {
                        size: isMobile ? 11 : 12,
                        family: "'Figtree', sans-serif",
                    },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(37, 19, 22, 0.95)',
                titleColor: 'white',
                bodyColor: 'rgba(255, 255, 255, 0.85)',
                borderColor: 'rgba(255, 255, 255, 0.08)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                padding: 12,
                titleFont: {
                    size: 13,
                    weight: 'bold',
                    family: "'Figtree', sans-serif",
                },
                bodyFont: {
                    size: 12,
                    family: "'Figtree', sans-serif",
                },
                boxPadding: 4,
            },
        },
        scales: type !== 'doughnut' && type !== 'pie' && type !== 'radar' && type !== 'polarArea' ? {
            x: {
                ticks: {
                    font: {
                        size: isMobile ? 10 : 12,
                        family: "'Figtree', sans-serif",
                    },
                    color: '#7A6A5F',
                    maxRotation: isMobile ? 45 : 0,
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
            },
            y: {
                ticks: {
                    font: {
                        size: isMobile ? 10 : 12,
                        family: "'Figtree', sans-serif",
                    },
                    color: '#7A6A5F',
                },
                grid: {
                    color: 'rgba(43, 33, 29, 0.08)',
                    // @ts-ignore -- Chart.js supports this but types may lag
                    borderDash: [4, 4],
                },
                border: {
                    display: false,
                },
            },
        } : undefined,
        elements: {
            bar: {
                borderRadius: 6,
                borderSkipped: false,
            },
            arc: {
                borderWidth: 2,
                borderColor: '#FFFDFC',
            },
        },
        cutout: type === 'doughnut' ? '60%' : undefined,
        ...options,
    };

    if (loading) {
        return (
            <div className={`${variant === 'card' ? 'bg-white/95 rounded-xl shadow border border-monday-border-light p-6' : ''} ${className}`}>
                {title && (
                    <h3 className="text-base font-semibold text-monday-text mb-4">{title}</h3>
                )}
                <div className="mobile-chart-container">
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-monday-border-light border-t-primary-500 mx-auto mb-2"></div>
                            <p className="text-sm text-monday-text-muted">Loading chart...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // sanitize data defensively to avoid runtime errors
    const safeLabels = Array.isArray((data as any).labels)
        ? ((data as any).labels as any[]).map((l) => (l == null ? '' : String(l)))
        : [];
    const safeDatasets = Array.isArray((data as any).datasets)
        ? (data as any).datasets.map((ds: any, idx: number) => {
            const color = ds?.borderColor || MONDAY_CHART_PALETTE[idx % MONDAY_CHART_PALETTE.length];
            const baseDataset = {
                ...ds,
                label: ds?.label || 'Series',
                data: Array.isArray(ds?.data)
                    ? ds.data.map((v: any) => {
                        if (v == null) return null;
                        const n = Number(v);
                        return Number.isFinite(n) ? n : null;
                    })
                    : [],
                spanGaps: true,
            };

            // Apply line chart enhancements
            if (type === 'line') {
                return {
                    ...baseDataset,
                    borderColor: color,
                    backgroundColor: ds?.backgroundColor || hexToFillColor(typeof color === 'string' ? color : MONDAY_CHART_PALETTE[idx % MONDAY_CHART_PALETTE.length]),
                    tension: ds?.tension ?? 0.4,
                    fill: ds?.fill ?? true,
                    borderWidth: ds?.borderWidth ?? 2,
                    pointRadius: ds?.pointRadius ?? 0,
                    pointHoverRadius: ds?.pointHoverRadius ?? 5,
                    pointHoverBackgroundColor: color,
                    pointHoverBorderColor: '#fff',
                    pointHoverBorderWidth: 2,
                };
            }

            return baseDataset;
        })
        : [];

    // Check for empty data
    const hasData = safeDatasets.length > 0 && safeDatasets.some((ds: any) =>
        Array.isArray(ds.data) && ds.data.some((v: any) => v !== null && v !== undefined)
    );

    const safeData: ChartData<any> = {
        labels: safeLabels,
        datasets: safeDatasets,
    } as any;

    return (
        <div className={`${variant === 'card' ? 'bg-white/95 rounded-xl shadow border border-monday-border-light p-4 md:p-6' : ''} ${className}`}>
            {title && (
                <h3 className="text-base font-bold tracking-tight text-monday-text mb-4 text-center md:text-left">
                    {title}
                </h3>
            )}
            <div
                className="mobile-chart-container"
                style={{ height: isMobile ? '300px' : height }}
            >
                {hasData ? (
                    <ChartErrorBoundary>
                        <ReactChart
                            key={instanceKey}
                            type={type}
                            data={safeData}
                            options={mobileOptions}
                        />
                    </ChartErrorBoundary>
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <svg className="mx-auto h-12 w-12 text-monday-status-neutral mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <p className="text-monday-text font-medium text-sm mb-1">No data to display</p>
                            <p className="text-monday-text-muted text-xs">Add records to see insights here</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResponsiveChart;
